import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Modal, InlineSpinner } from './UI';
import { SignatureInput, TaeppTitle } from './TaeppUI';

// An admin signs a claim to verify it, or the TAEPP approver signs it to approve it.
// The claim statement is shown as it is now (every signature so far), then the signer
// adds their e-signature (their saved one by default, or draws/uploads a new one) and
// confirms. The server refuses if someone else signed first and says who and when, which
// is shown here; onChanged() then lets the queue refresh itself.
// Styles: `.taepp .sgn-*` in index.css.

const COPY = {
  verify: {
    title: 'Verify Claim', button: 'Sign and Verify', url: 'verify',
    confirm: "I've checked this claim and I'm signing to verify it.",
  },
  approve: {
    title: 'Approve Claim', button: 'Sign and Approve', url: 'approve',
    confirm: "I've reviewed this claim and I'm signing to approve it.",
  },
};

function Step({ n, title, area, last, children }) {
  return (
    <section className={`sgn-step sgn-a-${area}${last ? ' last' : ''}`}>
      <span className="sgn-num" aria-hidden="true">{n}</span>
      <div className="sgn-step-body">
        <h3 className="sgn-step-t">{title}</h3>
        {children}
      </div>
    </section>
  );
}

export default function ClaimSignOffModal({ show, claim, mode, subtitle, onClose, onChanged }) {
  const copy = COPY[mode] || COPY.verify;
  const [saved, setSaved] = useState(null);             // null until loaded
  const [signature, setSignature] = useState(null);     // data URL, '' = use saved, null = nothing yet
  const [confirmed, setConfirmed] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const claimId = claim?.id;

  useEffect(() => {
    if (!show) return undefined;
    let stale = false;
    setSaved(null); setSignature(null); setConfirmed(false); setError(''); setSaving(false);
    api.get('/trainers/claims/signoff-info')
      .then(r => { if (!stale) setSaved(r.data.data); })
      .catch(() => { if (!stale) setSaved({ has_signature: false, image: null }); });
    return () => { stale = true; };
  }, [show, claimId]);

  // The statement as it will look once signed: re-drawn whenever the signature changes.
  useEffect(() => {
    if (!show || !saved) return undefined;
    let stale = false;
    setLoadingPdf(true);
    api.post(`/trainers/claims/${claimId}/preview-signoff`, { role: mode, signature: signature || undefined }, { responseType: 'blob' })
      .then(res => { if (!stale) setPdfUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(res.data); }); })
      .catch(() => { if (!stale) setError('Failed to load the claim form.'); })
      .finally(() => { if (!stale) setLoadingPdf(false); });
    return () => { stale = true; };
  }, [show, claimId, mode, saved, signature]);

  useEffect(() => {
    if (show) return;
    setPdfUrl(prev => { if (prev) URL.revokeObjectURL(prev); return ''; });
  }, [show]);

  const hasSignature = signature === '' ? !!saved?.has_signature : !!signature;
  const canSign = hasSignature && confirmed && !saving;
  const missing = !hasSignature ? 'Add your signature' : !confirmed ? 'Tick the box to confirm' : '';

  const handleSign = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put(`/trainers/claims/${claimId}/${copy.url}`, signature ? { signature } : {});
      onChanged(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to sign this claim');
      // Someone else may have signed first: let the queue show the current status.
      if (err.response?.status === 409) onChanged(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onClose={() => !saving && onClose()} size="xl"
      title={<TaeppTitle icon="bi-patch-check" title={copy.title} subtitle={subtitle} />}>
      <div className="taepp sgn-grid two">
        <Step n="1" title="Review the Claim" area="preview" last>
          <div className="sgn-preview-bar">
            <span><i className="bi bi-file-earmark-pdf" aria-hidden="true" /> Trainers / Assessors Claim Form</span>
            <span className="tp-pill mute"><i className="bi bi-eye" aria-hidden="true" />Preview · Not Signed Yet</span>
          </div>
          <div className="sgn-paper">
            {pdfUrl && <iframe title="Trainers / Assessors Claim Form" src={`${pdfUrl}#view=FitH&toolbar=0&navpanes=0`} />}
            {loadingPdf && <div className="sgn-loading"><InlineSpinner /><span>Updating preview…</span></div>}
          </div>
        </Step>

        <Step n="2" title="Add Your Signature" area="sign">
          {saved ? (
            <SignatureInput key={String(saved.has_signature)} hasSaved={saved.has_signature} savedImage={saved.image} onChange={setSignature} />
          ) : (
            <div className="tp-hint"><InlineSpinner /></div>
          )}
        </Step>

        <Step n="3" title="Confirm" area="confirm" last>
          <label className="tp-check" style={{ alignItems: 'flex-start' }}>
            <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
            <span>{copy.confirm}</span>
          </label>
          {error && <div className="tp-error"><i className="bi bi-exclamation-circle" aria-hidden="true" /><span>{error}</span></div>}
          {!error && missing && !saving && <div className="tp-hint" style={{ marginTop: 10 }}><i className="bi bi-info-circle" aria-hidden="true" /> {missing} to continue.</div>}
          <div className="tp-actions" style={{ marginTop: 10 }}>
            <button type="button" className="tp-btn" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="button" className="tp-btn primary" disabled={!canSign} onClick={handleSign}>
              {saving ? <InlineSpinner /> : <><i className="bi bi-pen" aria-hidden="true" />{copy.button}</>}
            </button>
          </div>
        </Step>
      </div>
    </Modal>
  );
}
